import { useState } from "react";
import { toast } from "sonner";
import type { FreezeFormData, SelectedTicket } from "@/@types/freeze.types";
import { useCreateFreezeApplicationMutation } from "@/mutations/useCreateFreezeApplicationMutation";
import { useGetFreezeApplicationsQuery } from "@/queries/useGetFreezeApplicationsQuery";
import { uploadAttachment, addDocumentsToApplication } from "@/http/freezeApplicationsApi";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { formatDateShort } from "@/utils/dateFormatters";

const initialFormData: FreezeFormData = {
  reason: "",
  dateRangesText: "",
  files: [],
  selectedTicket: undefined,
};

export const useFreezeApplication = () => {
  const [formData, setFormData] = useState<FreezeFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const { authPhone } = useAuth();
  const { selectedClubId } = useClub();
  const { mutateAsync: createApplication } = useCreateFreezeApplicationMutation();
  const {
    data: existingApplications,
    isLoading: isLoadingApplications,
    refetch: refetchApplications,
  } = useGetFreezeApplicationsQuery();

  const handleReasonChange = (reason: string) => {
    setFormData((prev) => ({ ...prev, reason }));
  };

  const handleDateRangesTextChange = (dateRangesText: string) => {
    setFormData((prev) => ({ ...prev, dateRangesText }));
  };

  const handleTicketChange = (selectedTicket: SelectedTicket | undefined) => {
    setFormData((prev) => ({ ...prev, selectedTicket }));
  };

  const handleFileChange = (files: FileList | null) => {
    if (files && files.length > 0) {
      setFormData((prev) => ({
        ...prev,
        files: [...prev.files, ...Array.from(files)],
      }));
    }
  };

  const handleRemoveFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.selectedTicket) {
      toast.error("Выберите абонемент");
      return;
    }

    if (!formData.reason.trim()) {
      toast.error("Укажите причину заморозки");
      return;
    }

    if (!formData.dateRangesText.trim()) {
      toast.error("Укажите сроки заморозки");
      return;
    }

    if (formData.files.length === 0) {
      toast.error("Прикрепите подтверждающие документы");
      return;
    }

    if (!authPhone) {
      toast.error("Не удалось определить номер телефона");
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload files in parallel
      const uploadPromises = formData.files.map((file) => uploadAttachment(authPhone, file));
      const uploadResponses = await Promise.all(uploadPromises);
      const documentIds = uploadResponses.map((response) => response.data.data.id);

      // Format ticket_title with full info (name + dates)
      const selectedTicket = formData.selectedTicket;
      let ticketTitle = selectedTicket?.title || "";
      if (selectedTicket) {
        const startDate = formatDateShort(selectedTicket.active_date);
        const endDate = formatDateShort(selectedTicket.end_date);
        if (startDate || endDate) {
          ticketTitle += ` (${startDate}${endDate ? ` - ${endDate}` : ""})`;
        }
      }

      await createApplication({
        phone: authPhone,
        clubId: selectedClubId,
        data: {
          reason: formData.reason,
          date_ranges_text: formData.dateRangesText || undefined,
          documents: documentIds.length > 0 ? documentIds : undefined,
          ticket_id: selectedTicket?.item_id.replace(/-/g, ""),
          ticket_title: ticketTitle,
        },
      });

      // Refetch applications to show the new one
      refetchApplications();
      setSuccessDialogOpen(true);
      toast.success("Заявление отправлено");
    } catch (error) {
      console.error("Freeze application submit failed", error);
      toast.error("Ошибка при отправке заявления");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearApplication = () => {
    setFormData(initialFormData);
  };

  const uploadToExistingApplication = async (
    applicationId: number,
    existingDocumentIds: number[],
    files: File[]
  ): Promise<boolean> => {
    if (!authPhone || files.length === 0) return false;

    try {
      // Upload files in parallel
      const uploadPromises = files.map((file) => uploadAttachment(authPhone, file));
      const uploadResponses = await Promise.all(uploadPromises);
      const newDocumentIds = uploadResponses.map((response) => response.data.data.id);

      await addDocumentsToApplication(authPhone, applicationId, [
        ...existingDocumentIds,
        ...newDocumentIds,
      ]);

      refetchApplications();
      toast.success("Документы добавлены");
      return true;
    } catch (error) {
      console.error("Adding documents to freeze application failed", error);
      toast.error("Ошибка при загрузке документов");
      return false;
    }
  };

  return {
    formData,
    isSubmitting,
    successDialogOpen,
    setSuccessDialogOpen,
    existingApplications,
    isLoadingApplications,
    handleReasonChange,
    handleDateRangesTextChange,
    handleTicketChange,
    handleFileChange,
    handleRemoveFile,
    handleSubmit,
    handleClearApplication,
    uploadToExistingApplication,
  };
};
